import React, { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CInputGroup,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import Notifications from 'src/components/notifications/NotificationsToaster'

import { AuthContext } from 'src/contexts/authContext'
import { GlobalVariablesContext } from 'src/contexts/globalVariableContext'

import {
  useValidTempPassword,
  useValidUsername,
  useValidatedPassword,
} from 'src/hooks/useAuthHooks'
import { Password, Username, PasswordWithValidation } from 'src/components/auth/authComponents'

const FirstTimeForcePassword = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const authContext = useContext(AuthContext)

  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false)

  const [fetchError, setFetchError] = useState('')
  const { username, setUsername, usernameIsValid } = useValidUsername('')
  const { tempPassword, setTempPassword, tempPasswordIsValid } = useValidTempPassword('')
  const { password, setPassword, passwordIsValid } = useValidatedPassword('')
  const {
    password: passwordAgain,
    setPassword: setPasswordAgain,
    passwordIsValid: passwordAgainIsValid,
  } = useValidatedPassword('')
  const { forceUsername, forcePassword } = useContext(GlobalVariablesContext)

  const isValid =
    !usernameIsValid ||
    username.length === 0 ||
    !tempPasswordIsValid ||
    tempPassword.length === 0 ||
    password.length === 0 ||
    !passwordIsValid ||
    !passwordAgainIsValid ||
    password !== passwordAgain

  const signInClicked = async () => {
    setIsSpinnerVisible(true)
    try {
      await authContext.completeNewPasswordChallenge(username, tempPassword, password)
      const session = await cognito.getSession()
      const accessTokenDecoded = jwt(session.idToken.jwtToken)
      if (accessTokenDecoded.language) {
        i18n.changeLanguage(accessTokenDecoded.language.toLowerCase())
      }
    } catch (err) {
      resetForm()
      setFetchError(err.message)
    } finally {
      navigate('/')
      setIsSpinnerVisible(false)
    }
  }

  const resetForm = () => {
    document.getElementById('auth-first-time-password-form').reset()
    setPassword('')
  }

  useEffect(() => {
    if (forceUsername !== '') setUsername(forceUsername)
    if (forcePassword !== '') setTempPassword(forcePassword)
  }, [forceUsername, username, tempPassword, password])

  return (
    <div className='bg-body-tertiary min-vh-100 d-flex flex-row align-items-center'>
      {fetchError ? <Notifications type='error' message={fetchError} /> : null}
      <CContainer>
        <CRow className='justify-content-center'>
          <CCol md={5}>
            <CCard className='p-4'>
              <CCardBody>
                <CForm id='auth-first-time-password-form'>
                  <h1>{t('NewPassword.pageTitle')}</h1>
                  <CInputGroup className='mb-3'>
                    <Username
                      defaultValue={forceUsername}
                      usernameIsValid={usernameIsValid}
                      setUsername={setUsername}
                    />
                  </CInputGroup>
                  <CInputGroup className='mb-3'>
                    <Password
                      defaultValue={tempPassword}
                      label={t('Component.Auth.Placeholder.Code')}
                      isValid={tempPasswordIsValid}
                      setPassword={setTempPassword}
                    />
                  </CInputGroup>
                  <CInputGroup className='mb-3 d-block'>
                    <PasswordWithValidation
                      password={password}
                      setPassword={setPassword}
                      passwordAgain={passwordAgain}
                      setPasswordAgain={setPasswordAgain}
                    />
                  </CInputGroup>
                  <CButton
                    disabled={isValid}
                    color='primary'
                    onClick={signInClicked}
                    className='px-4 me-2'
                    tabIndex={-1}
                  >
                    {isSpinnerVisible ? (
                      <CSpinner color='light' variant='grow' size='sm' />
                    ) : (
                      t('NewPassword.button')
                    )}
                  </CButton>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default FirstTimeForcePassword
